import { Component, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import {
  AbstractComponent,
  ChatAvailabilityEnum,
  ChatCommandTypeEnum,
  ChatSocketService,
} from 'lib-trend-core';
import { take, takeUntil } from 'rxjs/operators';
import { ChatPreferencesDialogComponent } from '../chat-preferences/chat-preferences-dialog.component';
import * as ChatActions from '../state/chat.actions';
import { ChatShellService } from '../services/chat-shell.service';
import { statusesSelector } from '../state/chat.selectors';
import { ChatStatusEntry } from '../state/chat.state';
import {
  CHAT_AVAILABILITY_OPTIONS,
  CHAT_STATUS_EMOJIS,
  CHAT_STATUS_EXPIRY_PRESETS,
  isStatusExpired,
  resolveExpiryIso,
} from './chat-status.util';
import { ChatDialogService } from '../services/chat-dialog.service';

/** Free-text limit of the custom status (kept short on purpose — a status, not a bio). */
const STATUS_TEXT_MAX_LENGTH = 60;

/**
 * "Seu status" picker (SPEC-2026-07-internal-chat, T-21 · RF-29 / AC-25).
 *
 * Sends STATUS_SET `{ availability, statusEmoji?, statusText?, statusExpiresAt? }`
 * or STATUS_CLEAR over the websocket command path. Both are applied
 * optimistically to the store (`applyStatusUpdate` + `patchMySettings`) and
 * rolled back with the previous snapshot when the ack carries an error — the
 * authoritative value re-arrives on `chat:status:updated`, which the company
 * presence room broadcasts to me as well.
 *
 * STATUS_SET has REPLACE semantics server-side: an omitted emoji/text/expiry is
 * cleared, so the payload always mirrors exactly what the form shows.
 */
@Component({
  templateUrl: './chat-status-dialog.component.html',
  styleUrls: ['./chat-status-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatStatusDialogComponent extends AbstractComponent implements OnInit {

  readonly availabilityOptions = CHAT_AVAILABILITY_OPTIONS;
  readonly emojis = CHAT_STATUS_EMOJIS;
  readonly expiryPresets = CHAT_STATUS_EXPIRY_PRESETS;
  readonly textMaxLength = STATUS_TEXT_MAX_LENGTH;

  availability: ChatAvailabilityEnum = ChatAvailabilityEnum.ACTIVE;
  statusEmoji = '';
  statusText = '';
  expiryKey = 'NONE';

  submitting = false;
  clearing = false;

  /** Server-known snapshot — the rollback value when a command is refused. */
  private previous: ChatStatusEntry = {
    availability: ChatAvailabilityEnum.ACTIVE,
    statusEmoji: null,
    statusText: null,
    statusExpiresAt: null,
  };

  constructor(
    injector: Injector,
    private readonly dialogRef: MatDialogRef<ChatStatusDialogComponent>,
    private readonly dialog: ChatDialogService,
    private readonly store: Store,
    private readonly chatSocketService: ChatSocketService,
    private readonly chatShell: ChatShellService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    // One-shot hydration: the form must not jump under the user while an
    // unrelated colleague's status broadcast lands.
    this.store.select(statusesSelector)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((statuses) => {
        const mine = statuses[this.getIDCurrentUser()];
        const expired = isStatusExpired(mine);
        this.previous = {
          availability: mine?.availability ?? ChatAvailabilityEnum.ACTIVE,
          statusEmoji: mine?.statusEmoji ?? null,
          statusText: mine?.statusText ?? null,
          statusExpiresAt: mine?.statusExpiresAt ?? null,
        };
        this.availability = (expired ? ChatAvailabilityEnum.ACTIVE : mine?.availability) ?? ChatAvailabilityEnum.ACTIVE;
        this.statusEmoji = expired ? '' : (mine?.statusEmoji ?? '');
        this.statusText = expired ? '' : (mine?.statusText ?? '');
      });
  }

  get hasCustomStatus(): boolean {
    return !!this.statusEmoji || !!this.statusText.trim();
  }

  get busy(): boolean {
    return this.submitting || this.clearing;
  }

  selectAvailability(availability: ChatAvailabilityEnum): void {
    this.availability = availability;
  }

  selectEmoji(emoji: string): void {
    this.statusEmoji = this.statusEmoji === emoji ? '' : emoji;
  }

  close(): void {
    this.dialogRef.close();
  }

  /** Opens the global notification preferences (quiet hours + sound). */
  openPreferences(): void {
    this.dialogRef.close();
    this.dialog.open(ChatPreferencesDialogComponent, { width: '480px' });
  }

  /**
   * Jumps to the team-status view. The popup is single-pane, so the deep link
   * `/chat?view=team` is what both surfaces use.
   */
  openTeam(): void {
    this.dialogRef.close();
    this.chatShell.open({ view: 'team' });
  }

  save(): void {
    if (this.busy) return;

    const text = this.statusText.trim().slice(0, STATUS_TEXT_MAX_LENGTH);
    const emoji = this.statusEmoji || '';
    // Expiry only makes sense with a custom status to expire.
    const expiresAt = (emoji || text) ? resolveExpiryIso(this.expiryKey) : undefined;

    this.submitting = true;
    this.applyOptimistic({
      availability: this.availability,
      statusEmoji: emoji || null,
      statusText: text || null,
      statusExpiresAt: expiresAt ?? null,
    });

    this.chatSocketService.sendCommand(ChatCommandTypeEnum.STATUS_SET, {
      availability: this.availability,
      statusEmoji: emoji || undefined,
      statusText: text || undefined,
      statusExpiresAt: expiresAt,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.dialogRef.close(true),
        error: (ack) => {
          this.submitting = false;
          this.rollback();
          this.alertService.error(this.resolveAckError(ack?.errorKey));
        },
      });
  }

  clear(): void {
    if (this.busy) return;

    this.clearing = true;
    this.applyOptimistic({
      availability: ChatAvailabilityEnum.ACTIVE,
      statusEmoji: null,
      statusText: null,
      statusExpiresAt: null,
    });

    this.chatSocketService.sendCommand(ChatCommandTypeEnum.STATUS_CLEAR, {})
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.dialogRef.close(true),
        error: (ack) => {
          this.clearing = false;
          this.rollback();
          this.alertService.error(this.resolveAckError(ack?.errorKey));
        },
      });
  }

  trackEmoji(_index: number, emoji: string): string {
    return emoji;
  }

  // ---------------------------------------------------------------------------
  // Store reflection
  // ---------------------------------------------------------------------------

  private applyOptimistic(entry: ChatStatusEntry): void {
    this.store.dispatch(ChatActions.applyStatusUpdate({
      update: {
        userId: this.getIDCurrentUser(),
        availability: entry.availability,
        statusEmoji: entry.statusEmoji,
        statusText: entry.statusText,
        statusExpiresAt: entry.statusExpiresAt,
      },
    }));
    this.store.dispatch(ChatActions.patchMySettings({
      patch: {
        availability: entry.availability,
        statusEmoji: entry.statusEmoji ?? undefined,
        statusText: entry.statusText ?? undefined,
        statusExpiresAt: entry.statusExpiresAt ? new Date(entry.statusExpiresAt) : undefined,
      },
    }));
  }

  private rollback(): void {
    this.applyOptimistic(this.previous);
  }

  private resolveAckError(errorKey?: string): string {
    if (errorKey) {
      const translated = this.t(errorKey);
      if (translated !== errorKey) return translated;
    }
    return this.t('chat.errors.commandFailed');
  }
}
